import {
  createRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import BaseCard from "../../../components/ui/BaseCard";
import BaseTextField from "../../../components/ui/BaseTextField";
import { useDispatch, useSelector } from "react-redux";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import BaseRadio from "../../../components/ui/BaseRadio";
import { RadioGroup } from "@mui/material";
import PaymentTypeRadioLabel from "./PaymentTypeRadioLabel";
import {
  addEngineerExpense,
  removeEngineerExpense,
  resetEngineerPaymentFieldsAndValidation,
  updateEngineerExpense,
  updateWorkOrderForm,
  updateWorkOrderFormValidation,
} from "../../../store/workOrder/workOrderSlice";
import { CurrencySymbol, UserType, WorkOrderStatusType } from "../../../utils/enum";
import EngineerHourlyPay from "./EngineerPayFormTypes/EngineerHourlyPay";
import EngineerFTHPay from "./EngineerPayFormTypes/EngineerFTHPay";
import EngineerFullDayPay from "./EngineerPayFormTypes/EngineerFullDayPay";
import EngineerHalfDayPay from "./EngineerPayFormTypes/EngineerHalfDayPay";
import EngineerFixedPay from "./EngineerPayFormTypes/EngineerFixedPay";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import BaseButton from "../../../components/ui/BaseButton";
import RateComparisonModal from "../CreateWorkOrder/RateComparisonModal";
import { isStatusBetween } from "../../../utils/helpers";
import FieldLabel from "../../../components/ui/FieldLabel";

const EngineerPayForm = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const payment = useSelector((state) => state.workOrder.payment);
  const deletedEngineerExpenses = useRef([]);
  const rateButtonRef = useRef(null);
  const [openRateComparison, setOpenRateComparison] = useState(false);
  const [expenseFileRefs, setExpenseFileRefs] = useState([]);

  useEffect(() => {
    setExpenseFileRefs((refs) =>
      Array(payment.engineerExpenses?.length || 0)
        .fill()
        .map((_, i) => refs[i] || createRef()),
    );
  }, [payment.engineerExpenses]);

  const allCurrencies = Object.keys(CurrencySymbol);

  useImperativeHandle(ref, () => ({
    isValid: () => {
      const errors = [];

      const fail = (fieldName, message, errorMsgFieldName) => {
        errors.push(message);
        dispatch(updateWorkOrderFormValidation({ fieldName, data: true }));
        if (errorMsgFieldName) {
          dispatch(updateWorkOrderFormValidation({ fieldName: errorMsgFieldName, data: message }));
        }
      };

      if (payment.pay.engineerPaymentType === "HOURLY") {
        const { engineerMinimumHours, engineerAdditionalHourlyRate } = payment.pay;

        if (engineerMinimumHours && engineerMinimumHours > 0 && engineerAdditionalHourlyRate < 1) {
          fail("engineerAdditionalHourlyRate", "Engineer additional hourly rate is required");
        } else if (
          (!engineerMinimumHours || engineerMinimumHours === 0) &&
          engineerAdditionalHourlyRate > 0
        ) {
          fail(
            "engineerAdditionalHourlyRate",
            "Remove the engineer additional hourly rate, or set minimum hours",
          );
        } else {
          dispatch(
            updateWorkOrderFormValidation({ fieldName: "engineerMinimumHours", data: false }),
          );
          dispatch(
            updateWorkOrderFormValidation({
              fieldName: "engineerAdditionalHourlyRate",
              data: false,
            }),
          );
        }
      }

      if (payment.pay.engineerPaymentType === "HALF_DAY") {
        if (!payment.pay.engineerHalfDayRate && payment.pay.enableCustomRates) {
          fail("engineerHalfDayRate", "Engineer half day rate is required");
        }
      }

      if (payment.pay.engineerPaymentType === "FULL_DAY") {
        if (
          (!payment.pay.engineerFullDayRate || payment.pay.engineerFullDayRate <= 0) &&
          payment.pay.enableCustomRates
        ) {
          fail(
            "engineerFullDayRate",
            "Engineer full day rate is required",
            "engineerFullDayRateErrorMsg",
          );
        }
      }

      return { valid: errors.length === 0, errors };
    },
    getDeletedExpenses: () => deletedEngineerExpenses.current,
  }));
  useEffect(() => {
    const custPaymentType = payment.pay.paymentType;

    if (!payment.pay.engineerPaymentType || custPaymentTypeChanged(payment)) {
      const defaultType =
        custPaymentType === "FTH" || custPaymentType === "CUSTOM_HOURS"
          ? "HOURLY"
          : custPaymentType;

      dispatch(
        updateWorkOrderForm({
          step: "payment",
          data: {
            pay: {
              engineerPaymentType: defaultType,
            },
          },
        }),
      );
    }

    function custPaymentTypeChanged() {
      return (
        payment.pay.engineerPaymentType &&
        payment.pay.engineerPaymentType !== (custPaymentType === "FTH" ? "HOURLY" : custPaymentType)
      );
    }
  }, [dispatch, payment.pay.paymentType]);

  useEffect(() => {
    if (props.isEditMode && props.paymentData) {
      const data = props.paymentData;

      dispatch(
        updateWorkOrderForm({
          step: "payment",
          data: {
            pay: {
              paymentTerms: data.paymentTerms || "15 days",
              engineerPaymentType: data.paymentType || null,
              engineerHourlyRate: data.hourlyRate || null,
              engineerAdditionalHourlyRate: data.additionalHourlyRate || null,
              engineerMinimumHours: data.minimumHours || null,
              engineerFixedAmount: data.fixedAmount || null,
              engineerHalfDayRate: data.halfDayRate || null,
              engineerFullDayRate: data.fullDayRate || null,
              engineerFthRate: data.fthRate || null,
              engineerCurrency: data.currency || null,
              enableEngineerAdditionalHourRate: !!data.additionalHourlyRate,
              enableCustomRates: !!(
                data.hourlyRate ||
                data.halfDayRate ||
                data.fullDayRate ||
                data.fixedAmount
              ),
            },
            engineerExpenses: Array.isArray(data.expenses) ? data.expenses : [],
          },
        }),
      );
    }
  }, [props.isEditMode, props.paymentData, dispatch]);

  const isEnableCustomRates = isStatusBetween(
    props?.status,
    WorkOrderStatusType.ASSIGNED,
    WorkOrderStatusType.INVOICED,
  );
  useEffect(() => {
    if (props.isEditMode && isEnableCustomRates) {
      dispatch(
        updateWorkOrderForm({
          step: "payment",
          data: {
            pay: {
              enableCustomRates: true,
            },
          },
        }),
      );
    }
  }, [payment.pay.engineerPaymentType]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name !== "engineerCurrency" && name !== "paymentTerms") {
      dispatch(resetEngineerPaymentFieldsAndValidation());
      dispatch(
        updateWorkOrderForm({
          step: "payment",
          data: { pay: { enableCustomRates: false } },
        }),
      );
    }

    const updatedData =
      name === "engineerPaymentType" && value === "FIXED"
        ? {
            pay: {
              [name]: value,
              enableCustomRates: true,
            },
          }
        : {
            pay: {
              [name]: value,
            },
          };

    dispatch(updateWorkOrderForm({ step: "payment", data: updatedData }));
  };

  const handleExpenseChange = (index, event) => {
    const { name, value } = event.target;
    dispatch(updateEngineerExpense({ index, field: name, value }));
  };

  const handleAddExpense = () => {
    dispatch(addEngineerExpense());
  };

  const handleExpenseFileChange = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!payment.engineerExpenses[index]?.expenseKey) {
      dispatch(updateEngineerExpense({ index, field: "expenseKey", value: crypto.randomUUID() }));
    }

    dispatch(
      updateEngineerExpense({
        index,
        field: "attachment",
        value: { fileName: file.name, url: URL.createObjectURL(file) },
      }),
    );
  };

  const handleRemoveExpense = (id) => {
    const expense = payment.engineerExpenses.find((e) => e.id === id);

    if (expense?.addedBy?.role?.role === UserType.FREELANCER) {
      deletedEngineerExpenses.current.push(id);
    }

    dispatch(removeEngineerExpense(id));
  };
  const handleClickEngineerRateComparison = useCallback((e) => {
    e.stopPropagation();
    setOpenRateComparison((prev) => !prev);
  }, []);

  return (
    <BaseCard className="pt-4 pb-6 xl:px-16 px-5 mt-1 rounded-lg" elevation={1}>
      <div className="border-bottom mb-4">
        <h6 className="mb-2 text-dark">Engineer Pay</h6>
      </div>
      <div className="grid grid-cols-8 gap-x-8 gap-y-4 items-center xl:mb-5 mb-2 mt-5">
        <p className="font-normal 2xl:col-span-1 xl:col-span-2 lg:col-span-2 col-span-9">
          Payment Type
        </p>
        <div className="xl:col-span-3 lg:col-span-4  col-span-9">
          <RadioGroup aria-labelledby="demo-radio-buttons-group-label" name="radio-buttons-group">
            <div>
              <BaseRadio
                label={<PaymentTypeRadioLabel type="HOURLY" />}
                value="HOURLY"
                name="engineerPaymentType"
                checked={payment.pay.engineerPaymentType === "HOURLY"}
                onChange={handleChange}
              />
              <BaseRadio
                label={<PaymentTypeRadioLabel type="HALF_DAY" />}
                value="HALF_DAY"
                name="engineerPaymentType"
                checked={payment.pay.engineerPaymentType === "HALF_DAY"}
                onChange={handleChange}
              />
              <BaseRadio
                label={<PaymentTypeRadioLabel type="FULL_DAY" />}
                value="FULL_DAY"
                name="engineerPaymentType"
                checked={payment.pay.engineerPaymentType === "FULL_DAY"}
                onChange={handleChange}
              />
              <BaseRadio
                label={<PaymentTypeRadioLabel type="FIXED" />}
                value="FIXED"
                name="engineerPaymentType"
                checked={payment.pay.engineerPaymentType === "FIXED"}
                onChange={handleChange}
              />
            </div>
          </RadioGroup>
        </div>

        <div className="xl:col-span-3 lg:col-span-4 col-span-10  xl:mb-0 mb-2">
          <div className="relative w-full" ref={rateButtonRef}>
            <div
              className="bg-[#F0F0F0] py-1 px-4 rounded-[3px] border border-[#ABABAB] flex items-center justify-center cursor-pointer hover:bg-[#E5E5E5]"
              onClick={handleClickEngineerRateComparison}
            >
              <p className="text-sm ml-1">Rate comparison available</p>
            </div>

            {openRateComparison && (
              <RateComparisonModal
                open={openRateComparison}
                setOpenRateComparison={setOpenRateComparison}
                buttonRef={rateButtonRef}
                isEditMode={props.isEditMode}
              />
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-8 gap-x-8 gap-y-4 items-center xl:mb-5 mb-2 mt-5">
        <p className="font-normal 2xl:col-span-1 xl:col-span-2 lg:col-span-2 col-span-9">
          Payment terms
          <span className="text-red-500 ml-0.5" aria-hidden="true">
            *
          </span>
        </p>
        <div className="xl:col-span-2 lg:col-span-3  col-span-9">
          <BaseAutoSelect
            fullHeight={false}
            name="paymentTerms"
            onChange={handleChange}
            options={[
              { label: "15 Days", value: "15 days" },
              { label: "30 Days", value: "30 days" },
            ]}
            value={payment?.pay?.paymentTerms}
          />
        </div>
      </div>

      <div
        className={`flex gap-x-4 border-bottom mb-4 
        }`}
      >
        <h6 className="mb-2 text-gray-600">Custom Engineer Rates</h6>
        <BaseCheckbox
          id="enableCustomRates"
          name="enableCustomRates"
          checked={payment.pay.enableCustomRates || false}
          disabled={payment.pay.engineerPaymentType === "FIXED"} // disable for fixed type
          onChange={() => {
            // prevent toggle if FIXED
            if (payment.pay.engineerPaymentType === "FIXED") return;

            const isChecked = !payment.pay.enableCustomRates;
            dispatch(
              updateWorkOrderForm({
                step: "payment",
                data: {
                  pay: {
                    ...payment.pay,
                    enableCustomRates: isChecked,
                  },
                },
              }),
            );
          }}
        />
      </div>
      {/* A fieldset, not a div: `pointer-events-none` only blocks the mouse, so
          these inputs stayed reachable by Tab while custom rates were off — and
          anything typed there was silently nulled at save, because
          formatWorkOrderPayload gates every engineer field on the toggle.
          fieldset[disabled] disables every descendant control natively, so the
          block is inert by the same rule the payload uses. `min-w-0` undoes the
          default min-width:min-content that fieldsets carry. */}
      <fieldset
        disabled={!payment.pay.enableCustomRates}
        className={`grid grid-cols-2 gap-y-4 mt-3 mb-2 min-w-0 border-0 p-0 m-0 transition-opacity duration-300 ${
          !payment.pay.enableCustomRates ? "opacity-50" : ""
        }`}
      >
        <div className="lg:mr-10 lg:col-span-1 col-span-2 ">
          <p className="font-normal 2xl:col-span-1 xl:col-span-2 lg:col-span-2 col-span-9">
            Currency:
            {/* Required once custom engineer rates are switched on — the block
                above is disabled and dimmed until then. */}
            {payment.pay.enableCustomRates && (
              <span className="text-red-500 ml-0.5" aria-hidden="true">
                *
              </span>
            )}
          </p>
          <BaseAutoSelect
            required
            fullHeight={false}
            name="engineerCurrency"
            onChange={handleChange}
            options={allCurrencies.map((currency) => ({
              label: currency,
              value: currency,
            }))}
            value={payment?.pay?.engineerCurrency}
            disabled={!payment.pay.enableCustomRates}
          />
        </div>
      </fieldset>
      <div>
        {/* The rate inputs live in these sub-forms and carry no `disabled` of
            their own, so the dimming alone left them reachable by Tab while
            custom rates were off — and anything typed was nulled at save.
            fieldset[disabled] makes the block genuinely inert. */}
        <fieldset
          disabled={!payment.pay.enableCustomRates}
          className={`min-w-0 border-0 p-0 m-0 transition-opacity duration-300 ${
            !payment.pay.enableCustomRates ? "opacity-50" : ""
          }`}
        >
          {payment.pay.engineerPaymentType === "HOURLY" && <EngineerHourlyPay />}
          {payment.pay.engineerPaymentType === "FULL_DAY" && <EngineerFullDayPay />}
          {payment.pay.engineerPaymentType === "HALF_DAY" && <EngineerHalfDayPay />}
          {payment.pay.engineerPaymentType === "FIXED" && <EngineerFixedPay />}
        </fieldset>
      </div>

      <div>
        {(payment?.engineerExpenses ?? [])?.map((expense, index) => (
          <div key={expense?.id} className="bg-light rounded-lg pt-4 pb-8 lg:px-10 px-4 mt-10">
            <div className="flex justify-end">
              {expense?.addedBy?.role?.role !== UserType.FREELANCER && (
                <i
                  className="fa-regular fa-trash-can cursor-pointer"
                  onClick={() => handleRemoveExpense(expense?.id)}
                ></i>
              )}
            </div>
            <div className="grid grid-cols-2 gap-x-12 gap-y-4">
              <div className="lg:col-span-1 col-span-2">
                <BaseAutoSelect
                  fullHeight={false}
                  label="Expense"
                  name="expenseType"
                  labelShrink={false}
                  required
                  isDefaultField={false}
                  onChange={(e) => handleExpenseChange(index, e)}
                  options={[
                    { label: "Travel Expense", value: "TRAVEL" },
                    { label: "Tools Expense", value: "TOOLS" },
                    { label: "Material Expense", value: "MATERIAL" },
                    { label: "All Options", value: "ALL" },
                    { label: "Other", value: "OTHER" },
                  ]}
                  defaultValue={expense.expenseType}
                  value={expense.expenseType}
                  disabled={expense?.addedBy?.role?.role === UserType.FREELANCER}
                />
              </div>
              <div className="lg:col-span-1 col-span-2">
                <BaseTextField
                  fullHeight={false}
                  label="Description"
                  name="description"
                  labelShrink={false}
                  required
                  isDefaultField={false}
                  value={expense.description}
                  onChange={(e) => handleExpenseChange(index, e)}
                  disabled={expense?.addedBy?.role?.role === UserType.FREELANCER}
                />
              </div>

              <div className="lg:col-span-1 col-span-2">
                <BaseTextField
                  fullHeight={false}
                  label="Amount"
                  name="amount"
                  labelShrink={false}
                  required
                  type="float"
                  isDefaultField={false}
                  value={expense.amount}
                  onChange={(e) => handleExpenseChange(index, e)}
                  disabled={expense?.addedBy?.role?.role === UserType.FREELANCER}
                />
              </div>

              <div className="lg:col-span-1 col-span-2">
                {/* Same renderer as the Amount/Description labels above, so
                    the optional marker matches them exactly. */}
                <FieldLabel label="Attachment" requirement="optional" fullHeight={false} />

                <input
                  type="file"
                  ref={expenseFileRefs[index]}
                  onChange={(e) => handleExpenseFileChange(index, e)}
                  style={{ display: "none" }}
                />

                <div
                  style={{ boxShadow: "0px 0px 6px 0px rgba(0, 0, 0, 0.10)" }}
                  className="w-full flex h-[47px] justify-between items-center px-3 py-1 rounded-md"
                >
                  <div className="flex-1 min-w-0 rounded-md flex items-center">
                    {expense.attachment ? (
                      <>
                        {expense.attachment.url ? (
                          <a
                            href={expense.attachment.url}
                            download={expense.attachment.fileName}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="py-1 cursor-pointer truncate min-w-0 text-blue-500 hover:underline font-300"
                          >
                            {expense.attachment.fileName}
                          </a>
                        ) : (
                          <p className="py-1 font-300 truncate min-w-0">
                            {expense.attachment.fileName}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-gray-400 py-1 ml-2">No file selected</p>
                    )}
                  </div>

                  {expense?.addedBy?.role?.role !== UserType.FREELANCER && (
                    <BaseButton
                      variant="outlined"
                      color="primary"
                      className="h-[32px] ml-3 shrink-0 whitespace-nowrap !px-3"
                      onClick={() => expenseFileRefs[index]?.current?.click()}
                    >
                      Choose File
                    </BaseButton>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        <div className="inline-block mt-4 cursor-pointer" onClick={handleAddExpense}>
          <div className="flex items-center">
            <i className="fa-solid fa-plus text-secondary"></i>
            <p className="text-secondary text-sm ml-1">Add Expenses</p>
          </div>
        </div>
      </div>
    </BaseCard>
  );
});

export default EngineerPayForm;
